import { resetALL } from "../../../../../.globals/redux/actions";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { clientQuery } from '../../../../../core/data/GraphQLApi';
import { endpoints } from '../../../../../core/defaultValues';
import { checkDeviceData } from 'Core/data/Helpers'
import {ExceptionManager} from "Core/logManager";

const INIT_STATE = {
    loadingCoachingHistory: false,
    totalCoachingHistory: 0,
    coachingHistory: [],
    nextToken: null,

    loadingRiskHistory: false,
    totalRiskHistory: 0,
    riskHistory: [],

    loadingStatsSinceLastCoaching: false,
    statsSinceLastCoaching: [],
    dateRange: null,
};

export const searchCoachingHistoryByDriver = createAsyncThunk(
    "driverPerformance/overviewTab/searchCoachingHistoryByDriver",
    async (payload, { rejectWithValue, getState }) => {
        try {
            const { limit, conditions } = payload

            const state = getState();
            let pagination = payload.conditions.find((item) => item.page);
            const currentNextToken = pagination === 1 ? '' : state.driverPerformanceOverviewTabRedux.nextToken;
            const currentConditions = conditions.filter((item) => !item.page);

            let params = {
                limit,
                nextToken: currentNextToken || '',
                conditions: currentConditions,
                sort: payload?.sort || [{ field: 'completedDate', order: 'DESC' }],
            }

            const response = await clientQuery(
                `
                query searchDriverPerformanceCases ($conditions: [DriverPerformanceCaseSearchInput], $sort: [DriverPerformanceCaseSortInput], $limit: Int, $offset: Int, $nextToken: String) {
                    data: searchDriverPerformanceCases (conditions: $conditions, sort: $sort, limit: $limit, offset: $offset, nextToken: $nextToken) {
                        items {
                            id                  
                            caseNumber
                            driverId
                            statusId
                            status {
                                entityId
                                label
                                nextStep
                                nextStatusId
                            }
                            createdOn
                        }
                        nextToken
                        total
                    }
                }`,
                params,
                endpoints.GRAPHQL_GENERAL_V2
            );

            return response?.data ? response?.data : { items: [], nextToken: null };
        } catch (exc) {
            ExceptionManager(exc, route, 'searchCoachingHistoryByDriver');
            return rejectWithValue(exc);
        }
    }
);

export const searchRiskHistory = createAsyncThunk(
    "driverPerformance/overviewTab/searchRiskHistory",
    async (payload, { rejectWithValue, getState }) => {
        try {
            const { caseNumber } = payload
            const response = await clientQuery(
                `
                query getCaseSummary ($caseNumber: String!) {
                    data: getCaseSummary (caseNumber: $caseNumber){
                        eventName
                        priorCoaching
                        events {
                            unitTime
                            deviceLabel
                            unitId
                            driverName
                            eventName
                            eventCode
                            unitType
                            deviceData
                            extraFields
                        }
                    }
                }`,
                {
                    caseNumber: caseNumber
                },
                endpoints.GRAPHQL_GENERAL_V2
            );
            return response?.data || []
        } catch (exc) {
            return []
        }
    }
);

export const searchStatsSinceLastCoaching = createAsyncThunk(
    "driverPerformance/overviewTab/searchStatsSinceLastCoaching",
    async (payload, { rejectWithValue, getState }) => {
        try {
            const { caseId, driverId } = payload
            const response = await clientQuery(
                `
                query getDriverLivePerformance ($driverId: Int!, $caseId: Int!) {
                    data: getDriverLivePerformance (driverId: $driverId, caseId: $caseId)
                }`,
                {
                    driverId: driverId,
                    caseId: caseId
                },
                endpoints.GRAPHQL_GENERAL_V2
            );

            let data = checkDeviceData(response?.data)

            if (data) {
                const { dateRange, ...metrics } = data;
                const result = Object.entries(metrics).map(([key, value]) => ({
                    key,
                    value
                }));
                return { items: result || [], dateRange: dateRange || null }
            }

            return { items: [], dateRange: null }
        } catch (exc) {
            return []
        }
    }
);

//Slice
const driverPerformanceOverviewTabRedux = createSlice({
    name: "driverPerformance/overviewTab",
    initialState: INIT_STATE,
    reducers: {
        resetOverviewTab(state, action) {
            return action.payload ? { ...state, ...action.payload } : INIT_STATE;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(resetALL, () => INIT_STATE);

        //searchCoachingHistoryByDriver
        builder.addCase(searchCoachingHistoryByDriver.pending, (state, action) => {
            const { conditions } = action.meta.arg
            let pagination = conditions.find((item) => item.page);

            if (pagination?.page === 1) {
                state.coachingHistory = []
                state.nextToken = null
            }

            state.loadingCoachingHistory = true;
        })
        builder.addCase(searchCoachingHistoryByDriver.fulfilled, (state, action) => {
            const newItems = action.payload?.items || [];
            const nextToken = action.payload?.nextToken || null;
            const itemsWithCountId = newItems.map((item, index) => ({
                ...item,
                countId: state.coachingHistory.length + index + 1
            }));

            state.coachingHistory = [...state.coachingHistory, ...itemsWithCountId]
            state.nextToken = nextToken
            state.loadingCoachingHistory = false;
            state.totalCoachingHistory = action.payload.total;
        })
        builder.addCase(searchCoachingHistoryByDriver.rejected, (state, action) => {
            state.loadingCoachingHistory = false;
            state.error = action.payload;
        })

        //searchRiskHistoryByDriver
        builder.addCase(searchRiskHistory.pending, (state, action) => {
            state.loadingRiskHistory = true;
        })
        builder.addCase(searchRiskHistory.fulfilled, (state, action) => {
            state.loadingRiskHistory = false;
            state.riskHistory = action.payload;
        })
        builder.addCase(searchRiskHistory.rejected, (state, action) => {
            state.loadingRiskHistory = false;
            state.error = action.payload;
        })

        //searchStatsSinceLastCoaching
        builder.addCase(searchStatsSinceLastCoaching.pending, (state, action) => {
            state.loadingStatsSinceLastCoaching = true;
        })
        builder.addCase(searchStatsSinceLastCoaching.fulfilled, (state, action) => {
            state.loadingStatsSinceLastCoaching = false;
            state.statsSinceLastCoaching = action.payload?.items || [];
            state.dateRange = action.payload?.dateRange || null;
        })
        builder.addCase(searchStatsSinceLastCoaching.rejected, (state, action) => {
            state.loadingStatsSinceLastCoaching = false;
            state.error = action.payload;
        })
    },
});

// Extract and export the action creators object and the reducer
export const { actions, reducer } = driverPerformanceOverviewTabRedux;

// Extract and export each action creator by name
export const { resetOverviewTab } = actions;

// Export the reducer, either as a default or named export
export default reducer;
